import { getRole } from '@modusign/openapi-client-user';
import {
  RolePermissionResponse,
  RolePermissionResponseName,
} from '@modusign/openapi-client-user/generated/model';
import { useQuery } from 'react-query';

import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

import { useCurrentUserRole } from '../workspace';

/**
 * 모든 권한을 갖는 다는 의미의 alias 입니다.
 * 권한 중에 "ALL"이 있는 경우 : workspace의 admin 유저
 */
const ALL_PERMISSION_ALIAS = 'ALL';

export default function useMemberPermissions(permission?: RolePermissionResponseName) {
  const workspaceId = useWorkspaceId();
  const role = useCurrentUserRole();
  const roleId = role?.id;

  const {
    data: permissions,
    isLoading,
    isError,
    isSuccess,
  } = useQuery<RolePermissionResponse[], Error>({
    queryKey: ['permissions', workspaceId, roleId],
    queryFn: () => getRole(workspaceId, roleId || 'undefined').then((d) => d.permissions),
    staleTime: 5 * 1000,
    enabled: !!workspaceId && !!roleId,
  });

  return {
    permissions,
    isLoading,
    isError,
    isSuccess,
    isPermitted: !!permissions?.some(
      (p) => p.name === ALL_PERMISSION_ALIAS || p.name === permission
    ),
    permissionMap: getPermissionMap(permissions || []),
  };
}

function getPermissionMap(
  permissions: RolePermissionResponse[]
): Record<RolePermissionResponseName, boolean> {
  const hasAllPermission = permissions.some((p) => p.name === ALL_PERMISSION_ALIAS);

  const permissionMap: Record<RolePermissionResponseName, boolean> = {
    ALL: hasAllPermission,

    // MANAGE_WORKSPACE: '워크스페이스 이름 수정' // 워크스페이스 이름 수정
    MANAGE_MEMBER: hasAllPermission, // 인사 관리 (그룹에 멤버 추가, 멤버 역할 부여, 초대 생성, 초대 삭제, 멤버 삭제)
    MANAGE_GROUP: hasAllPermission, // 그룹 관리
    MANAGE_ROLE: hasAllPermission, // 역할 관리
    MANAGE_PAYMENT: hasAllPermission,
    MANAGE_CUSTOM_BRANDING: hasAllPermission, // 커스텀브랜딩 관리

    CREATE_DOCUMENT: hasAllPermission, // 서명 요청
    ASSIGN_DOCUMENT: hasAllPermission, // 문서 공유
    EXPORT_DOCUMENT: hasAllPermission, // 문서 추출 (다운로드, 완료 문서 전달, 필드값 추출, 감추서 다운로드)
    DELETE_DOCUMENT: hasAllPermission, // 문서 삭제
    READ_ALL_DOCUMENT: hasAllPermission, // 전체 문서 읽기

    CREATE_EXTERNAL_DOCUMENT: hasAllPermission, // 외부문서 생성
    UPDATE_EXTERNAL_DOCUMENT: hasAllPermission, // 외부문서 수정
    ASSIGN_EXTERNAL_DOCUMENT: hasAllPermission, // 외부문서 공유
    DELETE_EXTERNAL_DOCUMENT: hasAllPermission, // 외부문서 삭제
    READ_ALL_EXTERNAL_DOCUMENT: hasAllPermission, // 전체 외부문서 읽기

    CREATE_DOCUMENT_WITH_TEMPLATE: hasAllPermission, // 템플릿으로 서명 요청
    CREATE_TEMPLATE: hasAllPermission, // 템플릿 생성
    UPDATE_TEMPLATE: hasAllPermission, // 템플릿 수정
    ASSIGN_TEMPLATE: hasAllPermission, // 템플릿 공유
    DELETE_TEMPLATE: hasAllPermission, // 템플릿 삭제
    READ_ALL_TEMPLATE: hasAllPermission, // 전체 템플릿 읽기

    MANAGE_BULK_REQUEST: hasAllPermission, // 대량전송 관리
    READ_ALL_BULK_REQUEST: hasAllPermission, // 전체 대량전송 읽기

    MANAGE_CONTRACT_LINK: hasAllPermission, // 링크서명 관리
    READ_ALL_CONTRACT_LINK: hasAllPermission, // 전체 링크서명 읽기

    MANAGE_APPROVAL: hasAllPermission, // 내부결재 관리
    READ_ALL_APPROVAL: hasAllPermission, // 전체 내부결재 읽기

    MANAGE_EDITING_DOCUMENT: hasAllPermission, // 편집문서 관리
    READ_ALL_EDITING_DOCUMENT: hasAllPermission, // 전체 편집문서 읽기

    MANAGE_FORM_DRAFT: hasAllPermission, // 서식 관리
    READ_ALL_FORM_DRAFT: hasAllPermission, // 전체 서식 읽기

    CREATE_SIGNATURE: hasAllPermission, // 사인/도장 생성
    UPDATE_SIGNATURE: hasAllPermission, // 사인/도장 수정
    ASSIGN_SIGNATURE: hasAllPermission, // 사인/도장 공유
    EXPORT_SIGNATURE: hasAllPermission, // 사인/도장 다운로드
    DELETE_SIGNATURE: hasAllPermission, // 사인/도장 삭제
    READ_ALL_SIGNATURE: hasAllPermission, // 전체 사인/도장 읽기
  };

  if (!hasAllPermission) {
    permissions.forEach((p) => {
      permissionMap[p.name] = true;
    });
  }

  return permissionMap;
}
